import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismButtonGroup,
  PrismComboSelectInput,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDropdownsOtaDropdown from 'lumen/components/general/dropdowns/ota-dropdown';
import {
  SEARCH_INPUT_ICONS,
  type SearchInputIconType,
} from 'lumen/components/hotel/settings/central-mapping/tables/table-mapping';
import SettingsCentralMappingDropdownsDropdownCategoryFilter from 'lumen/components/settings/central-mapping/dropdowns/dropdown-category-filter';
import type { CentralMappingCategory } from 'lumen/models';
import {
  type BarBasedOptionId,
  type Breakdown,
  BREAKDOWNS,
  CENTRAL_MAPPING_BAR_BASED_OPTION_ID,
  CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID,
  CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS,
  CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID,
  CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID,
  type CommissionableOptionId,
  type IncludeArchivedValueOptionId,
  type SortDirectionOptionId,
  type ViewFilterOptionId,
} from 'lumen/utils/central-mapping-constants';

import styles from './dropdown-mapping-filter.module.css';

export type MarketFilterOption = {
  id: string;
  label: string;
};

export type ViewFilterOption = {
  id: ViewFilterOptionId;
  label: string;
};

export type BarBasedOption = {
  id: BarBasedOptionId;
  label: string;
};

export type CommissionableOption = {
  id: CommissionableOptionId;
  label: string;
};

export type AppliedMappingFilterIds = {
  barBasedId?: BarBasedOptionId;
  brandIds?: string[];
  categoryIds?: string[];
  chainIds?: string[];
  commissionableId?: CommissionableOptionId;
  includeArchivedId?: IncludeArchivedValueOptionId;
  propertyIds?: string[];
  search?: string;
  sortDirectionId?: SortDirectionOptionId;
  viewId?: ViewFilterOptionId;
};

export type AppliedFilterOptions = {
  barBased?: BarBasedOption;
  brands?: MarketFilterOption[];
  categories?: string[];
  chains?: MarketFilterOption[];
  commissionable?: CommissionableOption;
  includeArchived?: IncludeArchivedValueOptionId;
  properties?: MarketFilterOption[];
  search?: string;
  sortDirection?: SortDirectionOptionId;
  view?: ViewFilterOption;
};

interface SettingsCentralMappingDropdownsDropdownMappingFilterSignature {
  Args: {
    appliedFilters?: AppliedMappingFilterIds;
    applyFilters: (appliedFilters: AppliedMappingFilterIds) => void;
    brandOptions?: MarketFilterOption[];
    breakdown?: Breakdown;
    chainOptions?: MarketFilterOption[];
    hideSubscriptionFilters?: boolean;
    hierarchyCategories?: CentralMappingCategory[];
    isOpen?: boolean;
    onToggleDropdown?: (isOpen: boolean) => void;
    propertyOptions?: MarketFilterOption[];
    showCommissionableFilter?: boolean;
  };
}

// @ts-expect-error: Incorrect type
export default class SettingsCentralMappingDropdownsDropdownMappingFilter extends GeneralDropdownsOtaDropdown<SettingsCentralMappingDropdownsDropdownMappingFilterSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  @tracked appliedBrandOptions: MarketFilterOption[] = [];
  @tracked appliedCategoryIds: string[] = [];
  @tracked appliedChainOptions: MarketFilterOption[] = [];
  @tracked appliedPropertyOptions: MarketFilterOption[] = [];
  @tracked barBasedFilterOption: BarBasedOption = {
    id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL,
    label: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL,
  };
  @tracked commissionableFilterOption: CommissionableOption = {
    id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL,
    label: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL,
  };
  @tracked includeArchivedValueOption: IncludeArchivedValueOptionId =
    CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE;
  @tracked searchInputValue: string = '';
  @tracked sortDirectionValueOption: SortDirectionOptionId =
    CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;
  @tracked viewValueOption: ViewFilterOption = {
    id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL,
    label: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL,
  };

  // `dropdownClass` is used by OTA dropdown for the outside click detection (see ota-dropdown component)
  dropdownClass = '.dropdown-mapping-filter';
  initialAppliedFilterOptions: AppliedFilterOptions = {};
  styles = styles;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get appliedBrandsValue() {
    const appliedBrandIds = this.appliedBrandOptions.map((brand) => brand.id);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.brandOptions.filter((brandOption) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      appliedBrandIds.includes(brandOption.id),
    );
  }

  set appliedBrandsValue(value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.appliedBrandOptions = value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get appliedChainsValue() {
    const appliedChainIds = this.appliedChainOptions.map((chain) => chain.id);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.chainOptions.filter((chainOption) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      appliedChainIds.includes(chainOption.id),
    );
  }

  set appliedChainsValue(value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.appliedChainOptions = value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get appliedFiltersCount() {
    const appliedFiltersCount = Object.values(
      this.generalFilterChangeStatus,
    ).filter((value) => value).length;

    return appliedFiltersCount;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get appliedPropertiesValue() {
    const appliedPropertyIds = this.appliedPropertyOptions.map(
      (property) => property.id,
    );
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.propertyOptions.filter((propertyOption) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      appliedPropertyIds.includes(propertyOption.id),
    );
  }

  set appliedPropertiesValue(value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.appliedPropertyOptions = value ?? [];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get barBasedOptions() {
    return [
      {
        id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL,
        label: this.intl.t('general.all'),
      },
      {
        id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.YES,
        label: this.intl.t('general.yes'),
      },
      {
        id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.NO,
        label: this.intl.t('general.no'),
      },
      {
        id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.UNMAPPED,
        label: this.intl.t('general.unmapped'),
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get brandOptions() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.brandOptions ?? [];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chainOptions() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.chainOptions ?? [];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get commissionableOptions(): CommissionableOption[] {
    return [
      {
        id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL,
        label: this.intl.t('general.all'),
      },
      {
        id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.GROSS,
        label: this.intl.t('settings.central_mapping.gross'),
      },
      {
        id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NET,
        label: this.intl.t('settings.central_mapping.net'),
      },
      {
        id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NO,
        label: this.intl.t('general.no'),
      },
      {
        id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.UNMAPPED,
        label: this.intl.t('general.unmapped'),
      },
    ];
  }

  get defaultAppliedFilters(): AppliedFilterOptions {
    const defaultAppliedFilters: AppliedFilterOptions = {
      chains: [],
      brands: [],
      properties: [],
      categories: [],
    };

    defaultAppliedFilters.barBased = {
      id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL,
      label: this.intl.t('general.all'),
    };

    defaultAppliedFilters.commissionable = {
      id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL,
      label: this.intl.t('general.all'),
    };

    defaultAppliedFilters.sortDirection =
      CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;

    defaultAppliedFilters.includeArchived =
      CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE;

    defaultAppliedFilters.view = {
      id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL,
      label: this.intl.t('general.all'),
    };

    defaultAppliedFilters.search = '';

    return defaultAppliedFilters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterChangeStatus() {
    const hasChainChanged =
      this.appliedChainOptions.length !==
        this.initialAppliedFilterOptions.chains?.length ||
      this.appliedChainOptions.some((chain) => {
        return !this.initialAppliedFilterOptions.chains?.some(
          (defaultChain) => defaultChain.id === chain.id,
        );
      });
    const hasBrandChanged =
      this.appliedBrandOptions.length !==
        this.initialAppliedFilterOptions.brands?.length ||
      this.appliedBrandOptions.some((brand) => {
        return !this.initialAppliedFilterOptions.brands?.some(
          (defaultBrand) => defaultBrand.id === brand.id,
        );
      });
    const hasPropertyChanged =
      this.appliedPropertyOptions.length !==
        this.initialAppliedFilterOptions.properties?.length ||
      this.appliedPropertyOptions.some((property) => {
        return !this.initialAppliedFilterOptions.properties?.some(
          (defaultProperty) => defaultProperty.id === property.id,
        );
      });

    const hasBarBasedFilterChanged =
      (this.barBasedFilterOption?.id ??
        CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL) !==
      (this.initialAppliedFilterOptions.barBased?.id ??
        CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL);

    const hasCommissionableFilterChanged =
      (this.commissionableFilterOption?.id ??
        CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL) !==
      (this.initialAppliedFilterOptions.commissionable?.id ??
        CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL);

    const hasCategoryChanged =
      this.appliedCategoryIds.length !==
        this.initialAppliedFilterOptions.categories?.length ||
      this.appliedCategoryIds.some((categoryId) => {
        return !this.initialAppliedFilterOptions.categories?.includes(
          categoryId,
        );
      });

    return {
      hasChainChanged,
      hasBrandChanged,
      hasPropertyChanged,
      hasCategoryChanged,
      hasSearchInputChanged:
        this.searchInputValue !== this.initialAppliedFilterOptions.search,
      hasBarBasedFilterChanged,
      hasCommissionableFilterChanged,
      hasSortDirectionValueChanged:
        this.sortDirectionValueOption !==
        this.initialAppliedFilterOptions.sortDirection,
      hasIncludeArchivedValueChanged:
        this.includeArchivedValueOption !==
        this.initialAppliedFilterOptions.includeArchived,
      hasViewValueChanged:
        this.viewValueOption?.id !== this.initialAppliedFilterOptions.view?.id,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get generalFilterChangeStatus() {
    const hasChainChanged =
      this.appliedChainOptions.length !==
      this.defaultAppliedFilters.chains?.length;

    const hasBrandChanged =
      this.appliedBrandOptions.length !==
      this.defaultAppliedFilters.brands?.length;

    const hasPropertyChanged =
      this.appliedPropertyOptions.length !==
      this.defaultAppliedFilters.properties?.length;

    const hasCategoryChanged =
      this.appliedCategoryIds.length !==
      this.defaultAppliedFilters.categories?.length;

    const hasBarBasedFilterChange =
      (this.barBasedFilterOption?.id ??
        CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL) !==
      this.defaultAppliedFilters.barBased?.id;

    // Only counts when the commissionable filter is actually shown (Rate plans tab),
    // so a leftover value on another tab doesn't inflate the applied-filters badge.
    // @ts-expect-error: this.args is untyped on GeneralDropdownsOtaDropdown
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { showCommissionableFilter } = this.args;
    const hasCommissionableFilterChange =
      Boolean(showCommissionableFilter) &&
      (this.commissionableFilterOption?.id ??
        CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL) !==
        this.defaultAppliedFilters.commissionable?.id;

    return {
      hasChainChanged,
      hasBrandChanged,
      hasPropertyChanged,
      hasCategoryChanged,
      hasBarBasedFilterChange,
      hasCommissionableFilterChange,
      // Sort direction is purposely left out as it does not alter the number of rows shown
      hasIncludeArchivedValueChanged:
        this.includeArchivedValueOption !==
        this.defaultAppliedFilters.includeArchived,
      hasSearchInputChanged:
        this.searchInputValue !== this.defaultAppliedFilters.search,
      hasViewValueChanged:
        this.viewValueOption?.id !== this.defaultAppliedFilters.view?.id,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasChanges() {
    return (
      this.filterChangeStatus.hasChainChanged ||
      this.filterChangeStatus.hasBrandChanged ||
      this.filterChangeStatus.hasPropertyChanged ||
      this.filterChangeStatus.hasCategoryChanged ||
      this.filterChangeStatus.hasBarBasedFilterChanged ||
      this.filterChangeStatus.hasCommissionableFilterChanged ||
      this.filterChangeStatus.hasSortDirectionValueChanged ||
      this.filterChangeStatus.hasIncludeArchivedValueChanged ||
      this.filterChangeStatus.hasViewValueChanged ||
      this.filterChangeStatus.hasSearchInputChanged
    );
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get includeArchivedOptions() {
    return [
      {
        id: CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE,
        label: this.intl.t('settings.central_mapping.filters.hide_archived'),
        icon: 'visibility-off',
      },
      {
        id: CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW,
        label: this.intl.t('settings.central_mapping.filters.show_archived'),
        icon: 'visibility-on',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isApplyDisabled() {
    return !this.hasChanges;
  }

  get isStayRestrictionRateGroupBreakdown(): boolean {
    // @ts-expect-error: this.args is untyped on GeneralDropdownsOtaDropdown
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return this.args.breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get propertyOptions() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.propertyOptions ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchIcon() {
    const isEditing = this.searchInputValue.length > 0;
    let name: SearchInputIconType = SEARCH_INPUT_ICONS.SEARCH;
    if (isEditing) {
      name = SEARCH_INPUT_ICONS.CROSS;
    }

    const onClick = isEditing
      ? // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        (event?: Event) => {
          event?.stopPropagation();
          if (name === SEARCH_INPUT_ICONS.CROSS) {
            this.searchInputValue = '';
          }
        }
      : undefined;

    return { name, onClick };
  }

  get showBarBasedFilter(): boolean {
    return !this.isStayRestrictionRateGroupBreakdown;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showIncludeArchivedFilter() {
    return this.access.isStaffUser;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortDirectionOptions() {
    return [
      {
        id: CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING,
        label: this.intl.t('settings.central_mapping.filters.sort_asc'),
      },
      {
        id: CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.DESCENDING,
        label: this.intl.t('settings.central_mapping.filters.sort_desc'),
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get viewOptions() {
    return [
      {
        id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL,
        label: this.intl.t('general.all'),
      },
      {
        id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.UNMAPPED,
        label: this.intl.t('general.unmapped'),
      },
      {
        id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.AUTOMATED,
        label: this.intl.t(
          'settings.central_mapping.mapping.filter_rows.automated',
        ),
      },
    ];
  }

  constructor(
    owner: Owner,
    args: SettingsCentralMappingDropdownsDropdownMappingFilterSignature['Args'],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    super(owner, args);

    this.setupInitialValues();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAppliedBrands() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const appliedBrandIds = this.args.appliedFilters?.brandIds ?? [];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.appliedBrandOptions = this.brandOptions.filter((brandOption) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return appliedBrandIds?.some(
        // @ts-expect-error: Incorrect type
        (appliedBrandId) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          appliedBrandId.toString() === brandOption.id.toString(),
      );
    });

    this.initialAppliedFilterOptions.brands = this.appliedBrandOptions;
  }

  setAppliedCategories(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.appliedCategoryIds = this.args.appliedFilters?.categoryIds ?? [];

    this.initialAppliedFilterOptions.categories = this.appliedCategoryIds;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAppliedChains() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const appliedChainIds = this.args.appliedFilters?.chainIds ?? [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.appliedChainOptions = this.chainOptions.filter((chainOption) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return appliedChainIds?.some(
        // @ts-expect-error: Incorrect type
        (appliedChainId) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          appliedChainId.toString() === chainOption.id.toString(),
      );
    });

    this.initialAppliedFilterOptions.chains = this.appliedChainOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAppliedProperties() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const appliedPropertyIds = this.args.appliedFilters?.propertyIds ?? [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.appliedPropertyOptions = this.propertyOptions.filter(
      // @ts-expect-error: Incorrect type
      (propertyOption) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return appliedPropertyIds?.some(
          // @ts-expect-error: Incorrect type
          (appliedPropertyId) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            appliedPropertyId.toString() === propertyOption.id.toString(),
        );
      },
    );

    this.initialAppliedFilterOptions.properties = this.appliedPropertyOptions;
  }

  setupInitialValues(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.appliedFilters) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        sortDirectionId,
        viewId,
        search,
        barBasedId,
        commissionableId,
        includeArchivedId,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } = this.args.appliedFilters;

      this.barBasedFilterOption = this.barBasedOptions.find(
        (option) => option.id === barBasedId,
      ) ?? {
        id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL,
        label: this.intl.t('general.all'),
      };

      this.commissionableFilterOption = this.commissionableOptions.find(
        (option) => option.id === commissionableId,
      ) ?? {
        id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL,
        label: this.intl.t('general.all'),
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.sortDirectionValueOption =
        (sortDirectionId &&
          this.sortDirectionOptions.find(
            (option) => option.id === sortDirectionId,
          )?.id) ??
        CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;

      this.viewValueOption = this.viewOptions.find(
        (option) => option.id === viewId,
      ) ?? {
        id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL,
        label: this.intl.t('general.all'),
      };

      this.includeArchivedValueOption =
        this.includeArchivedOptions.find(
          (option) => option.id === includeArchivedId,
        )?.id ?? CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.searchInputValue = search ?? '';

      this.initialAppliedFilterOptions.barBased = this.barBasedFilterOption;
      this.initialAppliedFilterOptions.commissionable =
        this.commissionableFilterOption;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.initialAppliedFilterOptions.sortDirection = sortDirectionId;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.initialAppliedFilterOptions.includeArchived = includeArchivedId;
      this.initialAppliedFilterOptions.view = this.viewValueOption;
      this.initialAppliedFilterOptions.search = this.searchInputValue;
    }

    this.setAppliedChains();
    this.setAppliedBrands();
    this.setAppliedProperties();
    this.setAppliedCategories();
  }

  @action applyFilters(): void {
    const appliedFilters: AppliedMappingFilterIds = {};

    if (this.appliedChainOptions.length) {
      appliedFilters.chainIds = this.appliedChainOptions.map(
        (chain) => chain.id,
      );
    }

    if (this.appliedBrandOptions.length) {
      appliedFilters.brandIds = this.appliedBrandOptions.map(
        (brand) => brand.id,
      );
    }

    if (this.appliedPropertyOptions.length) {
      appliedFilters.propertyIds = this.appliedPropertyOptions.map(
        (property) => property.id,
      );
    }

    if (this.appliedCategoryIds.length) {
      appliedFilters.categoryIds = this.appliedCategoryIds;
    }

    if (this.barBasedFilterOption) {
      appliedFilters.barBasedId = this.barBasedFilterOption.id;
    }

    if (this.commissionableFilterOption) {
      appliedFilters.commissionableId = this.commissionableFilterOption.id;
    }

    if (this.sortDirectionValueOption) {
      appliedFilters.sortDirectionId = this.sortDirectionValueOption;
    }

    if (this.includeArchivedValueOption) {
      appliedFilters.includeArchivedId = this.includeArchivedValueOption;
    }

    if (this.viewValueOption) {
      appliedFilters.viewId = this.viewValueOption.id;
    }

    appliedFilters.search = this.searchInputValue.trim();

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.applyFilters(appliedFilters);
    this.closeDropdown();
  }

  @action clearAppliedBrands(): void {
    this.appliedBrandOptions = [];
  }

  @action clearAppliedCategory(): void {
    this.appliedCategoryIds = [];
  }

  @action clearAppliedChains(): void {
    this.appliedChainOptions = [];
  }

  @action clearAppliedProperties(): void {
    this.appliedPropertyOptions = [];
  }

  @action clearIncludeArchived(): void {
    this.includeArchivedValueOption =
      CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE;
  }

  @action closeDropdown(): void {
    // @ts-expect-error: Incorrect type
    this.isOpen = false;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.onToggleDropdown?.(false);
    this.setupInitialValues(); // Reset the applied filters when the dropdown is closed
  }

  @action onCancel(): void {
    this.toggleDropdown();
  }

  @action
  onChangebarBasedFilter(barBasedFilter: BarBasedOption): void {
    this.barBasedFilterOption = barBasedFilter;
  }

  @action
  onChangeIncludeArchivedValue(value: string | string[]): void {
    this.includeArchivedValueOption = value as IncludeArchivedValueOptionId;
  }

  @action
  onChangeSortDirectionValue(direction: string | string[]): void {
    this.sortDirectionValueOption = direction as SortDirectionOptionId;
  }

  @action
  onChangeViewValue(view: ViewFilterOption): void {
    this.viewValueOption = view;
  }

  @action onClearAll(): void {
    this.appliedChainOptions = [];
    this.appliedBrandOptions = [];
    this.appliedPropertyOptions = [];
    this.appliedCategoryIds = [];
    this.includeArchivedValueOption =
      CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE;

    this.barBasedFilterOption = {
      id: CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL,
      label: this.intl.t('general.all'),
    };

    this.commissionableFilterOption = {
      id: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL,
      label: this.intl.t('general.all'),
    };

    this.sortDirectionValueOption =
      CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;

    this.viewValueOption = {
      id: CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL,
      label: this.intl.t('general.all'),
    };

    this.searchInputValue = '';
  }

  @action
  setSearchInputValue(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = target ? target.value : '';
    this.searchInputValue = value;
  }

  @action setSelectedCategories(categoryIds: string[]): void {
    this.appliedCategoryIds = categoryIds;
  }

  @action toggleDropdown(): void {
    // Sync the internal isOpen state with the external arg value before toggling
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.isOpen !== this.isOpen) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.isOpen = this.args.isOpen ?? false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    super.toggleDropdown();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.onToggleDropdown?.(this.isOpen);

    // @ts-expect-error: Incorrect type
    if (!this.isOpen) {
      this.setupInitialValues(); // Reset the applied filters when the dropdown is closed
    }
  }

  <template>
    <div class={{this.styles.container}}>
      <PrismButton
        @buttonType="secondary"
        @iconBefore="filter"
        @label={{t "settings.central_mapping.filters"}}
        @onClick={{this.toggleDropdown}}
        data-test-id="dropdown-mapping-filter"
      >
        {{#if this.appliedFiltersCount}}
          <PrismBadge
            @isSmall={{true}}
            @label={{this.appliedFiltersCount}}
            @type="info"
          />
        {{/if}}
      </PrismButton>

      {{#if @isOpen}}
        <div
          class={{concat this.styles.dropdown " " "dropdown-mapping-filter"}}
          data-test-id="mapping-filter-dropdown"
        >
          <div class={{this.styles.body}}>
            <div class={{this.styles.row}}>
              <p class="text-200--bold">
                {{t "settings.central_mapping.filters.sort_value"}}
              </p>

              <div>
                <PrismButtonGroup
                  @onChange={{this.onChangeSortDirectionValue}}
                  @value={{this.sortDirectionValueOption}}
                  data-test-id="dropdown-sort-by-direction"
                  as |ButtonGroup|
                >
                  {{#each this.sortDirectionOptions as |sortDirectionOption|}}
                    <ButtonGroup.button
                      @label={{sortDirectionOption.label}}
                      @value={{sortDirectionOption.id}}
                      data-test-id={{concat
                        "dropdown-sort-by-direction-"
                        sortDirectionOption.id
                      }}
                    />
                  {{/each}}
                </PrismButtonGroup>
              </div>
            </div>

            <div class="divider-line m-0 w-full"></div>

            {{#if this.showIncludeArchivedFilter}}
              <div class={{this.styles.row}}>
                <div class="flex flex-col">
                  <p class="text-200--bold">
                    {{t "settings.central_mapping.filters.archived_values"}}
                  </p>
                  <p class="text-200--regular text-neutral-subdued">
                    {{t "settings.central_mapping.filters.staff_only"}}
                  </p>
                </div>

                <div>
                  <PrismButtonGroup
                    @onChange={{this.onChangeIncludeArchivedValue}}
                    @value={{this.includeArchivedValueOption}}
                    as |ButtonGroup|
                  >
                    {{#each
                      this.includeArchivedOptions
                      as |includeArchivedOption|
                    }}
                      <ButtonGroup.button
                        @iconBefore={{includeArchivedOption.icon}}
                        @label={{includeArchivedOption.label}}
                        @value={{includeArchivedOption.id}}
                        data-test-id={{concat
                          "dropdown-include-archived-"
                          includeArchivedOption.id
                        }}
                      />
                    {{/each}}
                  </PrismButtonGroup>
                </div>
              </div>
            {{/if}}

            <div class={{this.styles.row}}>
              <p class="text-200--bold">
                {{t "settings.central_mapping.filters.view"}}
              </p>

              <div>
                <PrismSelectInput
                  @onChange={{fn (mut this.viewValueOption)}}
                  @options={{this.viewOptions}}
                  @value={{this.viewValueOption}}
                  data-test-id="dropdown-view-value"
                />
              </div>
            </div>

            {{#if this.showBarBasedFilter}}
              <div class={{this.styles.row}}>
                <p class="text-200--bold">
                  {{t "settings.central_mapping.mapping.bar_based"}}
                </p>

                <div>
                  <PrismSelectInput
                    @onChange={{fn (mut this.barBasedFilterOption)}}
                    @options={{this.barBasedOptions}}
                    @value={{this.barBasedFilterOption}}
                    data-test-id="dropdown-bar-based-value"
                  />
                </div>
              </div>
            {{/if}}

            {{#if @showCommissionableFilter}}
              <div class={{this.styles.row}}>
                <p class="text-200--bold">
                  {{t "settings.central_mapping.mapping.commissionable"}}
                </p>

                <div>
                  <PrismSelectInput
                    @onChange={{fn (mut this.commissionableFilterOption)}}
                    @options={{this.commissionableOptions}}
                    @value={{this.commissionableFilterOption}}
                    data-test-id="dropdown-commissionable-value"
                  />
                </div>
              </div>
            {{/if}}

            <div class={{this.styles.column}}>
              <p class="text-200--bold">
                {{t "settings.central_mapping.filters.search_all"}}
              </p>

              <PrismInput
                @iconAfter={{this.searchIcon}}
                @name="search"
                @onInput={{this.setSearchInputValue}}
                @removePadding={{true}}
                @type="text"
                @value={{this.searchInputValue}}
                data-test-id="dropdown-search-value"
              />
            </div>

            <div class={{this.styles.column}}>
              <div class="flex justify-between">
                <p class="text-200--bold">
                  {{t "general.mapping"}}
                </p>

                <button
                  class="link-button--unstyled prism-link prism-link--small prism-link--standalone"
                  data-test-id="clear-applied-categories"
                  disabled={{eq this.appliedCategoryIds.length 0}}
                  type="button"
                  {{on "click" this.clearAppliedCategory}}
                >
                  {{t "settings.central_mapping.filters.clear"}}
                </button>
              </div>

              <div>
                <SettingsCentralMappingDropdownsDropdownCategoryFilter
                  @appliedCategoryIds={{this.appliedCategoryIds}}
                  @hierarchyCategories={{@hierarchyCategories}}
                  @onChange={{this.setSelectedCategories}}
                />
              </div>
            </div>

            {{#unless @hideSubscriptionFilters}}
              <div class={{this.styles.column}}>
                <div class="flex justify-between">
                  <p class="text-200--bold">
                    {{t "settings.central_mapping.chain"}}
                  </p>

                  <button
                    class="link-button--unstyled prism-link prism-link--small prism-link--standalone"
                    data-test-id="clear-applied-chains"
                    disabled={{eq this.appliedChainOptions.length 0}}
                    type="button"
                    {{on "click" this.clearAppliedChains}}
                  >
                    {{t "settings.central_mapping.filters.clear"}}
                  </button>
                </div>

                <div>
                  <PrismComboSelectInput
                    @isSearchable={{true}}
                    @onChange={{fn (mut this.appliedChainsValue)}}
                    @options={{this.chainOptions}}
                    @unsafe_isMultipleImmediate={{true}}
                    @unsafe_renderInPlace={{false}}
                    @value={{this.appliedChainsValue}}
                    data-test-id="dropdown-chain-value"
                  />
                </div>
              </div>

              <div class={{this.styles.column}}>
                <div class="flex justify-between">
                  <p class="text-200--bold">
                    {{t "settings.central_mapping.brand"}}
                  </p>

                  <button
                    class="link-button--unstyled prism-link prism-link--small prism-link--standalone"
                    data-test-id="clear-applied-brands"
                    disabled={{eq this.appliedBrandOptions.length 0}}
                    type="button"
                    {{on "click" this.clearAppliedBrands}}
                  >
                    {{t "settings.central_mapping.filters.clear"}}
                  </button>
                </div>

                <div>
                  <PrismComboSelectInput
                    @isSearchable={{true}}
                    @onChange={{fn (mut this.appliedBrandsValue)}}
                    @options={{this.brandOptions}}
                    @unsafe_isMultipleImmediate={{true}}
                    @unsafe_renderInPlace={{false}}
                    @value={{this.appliedBrandsValue}}
                    data-test-id="dropdown-brand-value"
                  />
                </div>
              </div>

              <div class={{this.styles.column}}>
                <div class="flex justify-between">
                  <p class="text-200--bold">
                    {{t "settings.central_mapping.properties"}}
                  </p>

                  <button
                    class="link-button--unstyled prism-link prism-link--small prism-link--standalone"
                    data-test-id="clear-applied-properties"
                    disabled={{eq this.appliedPropertyOptions.length 0}}
                    type="button"
                    {{on "click" this.clearAppliedProperties}}
                  >
                    {{t "settings.central_mapping.filters.clear"}}
                  </button>
                </div>

                <div>
                  <PrismComboSelectInput
                    @isSearchable={{true}}
                    @onChange={{fn (mut this.appliedPropertiesValue)}}
                    @options={{this.propertyOptions}}
                    @unsafe_isMultipleImmediate={{true}}
                    @unsafe_renderInPlace={{false}}
                    @value={{this.appliedPropertiesValue}}
                    data-test-id="dropdown-property-value"
                  />
                </div>
              </div>
            {{/unless}}
          </div>

          <div class={{this.styles.footer}}>
            <div>
              <PrismButton
                @buttonType="ghost"
                @label={{t "settings.central_mapping.filters.clear_all"}}
                @onClick={{this.onClearAll}}
                data-test-id="clear-all-filters"
              />
            </div>

            <div>
              <PrismButton
                @buttonType="secondary"
                @label={{t "general.cancel"}}
                @onClick={{this.onCancel}}
                data-test-id="cancel-filters"
              />

              <PrismButton
                @buttonType="primary"
                @isDisabled={{this.isApplyDisabled}}
                @label={{t "general.apply"}}
                @onClick={{this.applyFilters}}
                data-test-id="apply-filters"
              />
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
